import { Link } from 'react-router';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAuth } from '../lib/auth-context';
import { useRelationshipIntelligenceOptional } from '../context/RelationshipIntelligenceContext';
import { getMicroActionStats, readMicroActionsStatus } from '../lib/micro-actions';
import {
  Heart,
  Activity,
  TrendingUp,
  ShieldCheck,
  CalendarClock,
  Sparkles,
  Users,
  ArrowRight,
} from 'lucide-react';

type RelationshipRecord = {
  id: string;
  name?: string;
  type?: string;
  isActive?: boolean;
  createdAt?: string;
};

type CheckInRecord = {
  relationshipId?: string;
  date?: string;
  timestamp?: string;
  rtiScore?: number;
};

function readRelationships(): RelationshipRecord[] {
  try {
    const raw = JSON.parse(localStorage.getItem('tension_relationships') || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function readCheckins(): CheckInRecord[] {
  try {
    const raw = JSON.parse(localStorage.getItem('tension_checkins') || '[]');
    return Array.isArray(raw) ? raw : [];
  } catch {
    return [];
  }
}

function scoreLabel(score: number): string {
  if (score <= 25) return 'Calm';
  if (score <= 45) return 'Stable';
  if (score <= 65) return 'Elevated';
  return 'High tension';
}

function dateValue(input?: string): number {
  if (!input) return 0;
  const v = new Date(input).getTime();
  return Number.isFinite(v) ? v : 0;
}

export default function UserDashboard() {
  const { user } = useAuth();
  const relationshipIntel = useRelationshipIntelligenceOptional();

  const relationships = readRelationships();
  const activeRelationshipId = localStorage.getItem('tension_active_relationship') || '';
  const activeRelationship =
    relationships.find((r) => r.id === activeRelationshipId) || relationships.find((r) => r.isActive) || null;

  const allCheckins = readCheckins();
  const relationshipCheckins = allCheckins
    .filter((c) => !activeRelationshipId || c.relationshipId === activeRelationshipId)
    .sort((a, b) => dateValue(b.timestamp || b.date) - dateValue(a.timestamp || a.date));

  const latestScore = Number(relationshipCheckins[0]?.rtiScore ?? 0);
  const recentSeven = relationshipCheckins.slice(0, 7);
  const avgSeven =
    recentSeven.length > 0
      ? Number((recentSeven.reduce((sum, c) => sum + Number(c.rtiScore || 0), 0) / recentSeven.length).toFixed(1))
      : 0;

  const microStats = getMicroActionStats();
  const microStatus = readMicroActionsStatus();
  return (
    <div className="mx-auto w-full max-w-6xl px-4 py-6 md:px-6 md:py-8">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900 dark:text-neutral-100">Your Relationship Dashboard</h1>
          <p className="mt-1 text-sm text-neutral-600 dark:text-neutral-400">
            One place to review account details, relationship status, check-in trends, and action progress.
          </p>
        </div>
        <Button asChild variant="outline" className="gap-2">
          <Link to="/home">
            Open Home
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="p-4 border-violet-200/70 bg-white/90 dark:border-violet-900/45 dark:bg-neutral-900/75">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Users className="h-4 w-4" />
            Account
          </div>
          <p className="mt-2 text-sm text-neutral-700 dark:text-neutral-300">{user?.email || 'Not signed in'}</p>
          <p className="mt-1 text-xs text-neutral-500">User ID: {user?.id?.slice(0, 8) || 'unknown'}…</p>
        </Card>

        <Card className="p-4 border-violet-200/70 bg-white/90 dark:border-violet-900/45 dark:bg-neutral-900/75">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Heart className="h-4 w-4" />
            Active relationship
          </div>
          <p className="mt-2 text-base font-medium text-neutral-900 dark:text-neutral-100">
            {activeRelationship?.name || 'No active relationship'}
          </p>
          <p className="mt-1 text-xs text-neutral-500">
            {relationships.length} relationship{relationships.length === 1 ? '' : 's'} saved
          </p>
        </Card>

        <Card className="p-4 border-cyan-200/70 bg-white/90 dark:border-cyan-900/40 dark:bg-neutral-900/75">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Activity className="h-4 w-4" />
            Current score (RTI)
          </div>
          <p className="mt-2 text-2xl font-semibold text-neutral-900 dark:text-neutral-100">{latestScore.toFixed(1)}</p>
          <p className="mt-1 text-xs text-neutral-500">{scoreLabel(latestScore)}</p>
        </Card>

        <Card className="p-4 border-indigo-200/70 bg-white/90 dark:border-indigo-900/40 dark:bg-neutral-900/75">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <CalendarClock className="h-4 w-4" />
            Last 7 check-ins
          </div>
          <p className="mt-2 text-2xl font-semibold text-neutral-900 dark:text-neutral-100">{avgSeven.toFixed(1)}</p>
          <p className="mt-1 text-xs text-neutral-500">Average from {recentSeven.length} entries</p>
        </Card>
      </div>

      <div className="mt-4 grid gap-4 md:grid-cols-2">
        <Card className="p-4 border-fuchsia-200/70 bg-white/90 dark:border-fuchsia-900/40 dark:bg-neutral-900/75">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <Sparkles className="h-4 w-4" />
            Micro-actions
          </div>
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-neutral-100 p-2 dark:bg-neutral-800">
              <p className="text-lg font-semibold">{microStats.total}</p>
              <p className="text-xs text-neutral-500">Today</p>
            </div>
            <div className="rounded-lg bg-neutral-100 p-2 dark:bg-neutral-800">
              <p className="text-lg font-semibold">{microStats.completed}</p>
              <p className="text-xs text-neutral-500">Done</p>
            </div>
            <div className="rounded-lg bg-neutral-100 p-2 dark:bg-neutral-800">
              <p className="text-lg font-semibold">{Math.round(microStats.completionRate)}%</p>
              <p className="text-xs text-neutral-500">Completion</p>
            </div>
          </div>
          <p className="mt-3 text-xs text-neutral-500">
            Source: {microStatus?.source === 'atlas' ? 'Atlas live' : 'Local fallback'} {microStatus?.reason ? `(${microStatus.reason})` : ''}
          </p>
        </Card>

        <Card className="p-4 border-violet-200/70 bg-white/90 dark:border-violet-900/45 dark:bg-neutral-900/75">
          <div className="flex items-center gap-2 text-sm text-neutral-500">
            <TrendingUp className="h-4 w-4" />
            Relationship intelligence
          </div>
          <div className="mt-3 space-y-2 text-sm">
            <p className="text-neutral-700 dark:text-neutral-300">
              Weather: <span className="font-medium">{relationshipIntel?.model.weatherBand || 'Not enough data yet'}</span>
            </p>
            <p className="text-neutral-700 dark:text-neutral-300">
              Direction: <span className="font-medium">{relationshipIntel?.model.direction || 'Pending'}</span>
            </p>
            <p className="text-neutral-700 dark:text-neutral-300">
              Data strength: <span className="font-medium">{Math.round((relationshipIntel?.model.dataQuality || 0) * 100)}%</span>
            </p>
            <p className="text-neutral-700 dark:text-neutral-300">
              OS score: <span className="font-medium">{relationshipIntel?.osScore.overall?.toFixed(1) || '0.0'}</span>
            </p>
          </div>
        </Card>
      </div>

      <Card className="mt-4 border-violet-200/70 bg-white/90 p-4 dark:border-violet-900/45 dark:bg-neutral-900/75">
        <div className="mb-3 flex items-center gap-2 text-sm text-neutral-500">
          <ShieldCheck className="h-4 w-4" />
          Quick actions
        </div>
        <div className="flex flex-wrap gap-2">
          <Button asChild size="sm" variant="outline">
            <Link to="/check-in">Start check-in</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/timeline">Open timeline</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/insights">View insights</Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/settings">Manage settings</Link>
          </Button>
        </div>
      </Card>
    </div>
  );
}
